import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { api, type Profile, type LanguageOption, type BillingStatus } from "../api";
import LinkCard from "../components/LinkCard";
import Logo from "../components/common/Logo";
import { Button } from "../components/ui/Button";
import { cn } from "../lib/format";

// Display order for the plan picker — Trio gets the recommended badge
// because the price/limit gap from Daily makes it the sweet spot.
const PLAN_ORDER = ["taste", "daily", "trio", "polyglot"] as const;
type PlanId = typeof PLAN_ORDER[number];
const PLAN_TAGLINES: Record<PlanId, string> = {
  taste: "kick the tyres",
  daily: "one language, every morning",
  trio: "three at once, fluent faster",
  polyglot: "the whole shelf",
};

// Sample first-message previews — keyed by language code. Lets the dark
// preview card update as the user picks a language and types their name.
const SAMPLE_BY_LANG: Record<string, (name: string, time: string) => string> = {
  de: (n, t) => `Guten Morgen, ${n}. Pretty quiet at ${t} on a Tuesday — coffee yet?`,
  es: (n, t) => `Buenos días, ${n}. Tranquilo a las ${t} — ¿ya con café?`,
  fr: (n, t) => `Bonjour, ${n}. Encore calme à ${t} — déjà un café ?`,
  it: (n, t) => `Buongiorno, ${n}. Tranquillo alle ${t} — già un caffè?`,
  pt: (n, t) => `Bom dia, ${n}. Ainda tranquilo às ${t} — já tomou café?`,
  ja: (n, t) => `おはよう、${n}。${t}、静かな朝ですね — もうコーヒーは？`,
  nl: (n, t) => `Goedemorgen, ${n}. Het is nog rustig om ${t} — al koffie gehad?`,
  ru: (n, t) => `Доброе утро, ${n}. Тихо в ${t} — уже выпил кофе?`,
  ko: (n, t) => `좋은 아침, ${n}. ${t}, 조용하네요 — 커피 한 잔?`,
  zh: (n, t) => `早上好，${n}。${t}，安静的早晨 — 喝咖啡了吗？`,
  ar: (n, t) => `صباح الخير يا ${n}. هادئ الساعة ${t} — هل شربت قهوة بعد؟`,
};

// The five most-requested target languages surface as chips; the rest
// live behind a "…more" affordance so the picker stays scannable.
const PRIMARY_LANGS = ["de", "es", "fr", "it", "pt"];
const CEFR_OPTIONS = ["A1", "A2", "B1", "B2", "C1", "C2"];

// Full 12-language fallback — mirrors the teachable list from
// src/lang/registry.py. Used while /api/languages is in flight so the
// picker always renders something. Ordered PRIMARY_LANGS-first, rest
// after, so the row layout matches the loaded catalog.
const FALLBACK_LANGS: LanguageOption[] = [
  { code: "de", name: "German", endonym: "Deutsch" },
  { code: "es", name: "Spanish", endonym: "Español" },
  { code: "fr", name: "French", endonym: "Français" },
  { code: "it", name: "Italian", endonym: "Italiano" },
  { code: "pt", name: "Portuguese", endonym: "Português" },
  { code: "nl", name: "Dutch", endonym: "Nederlands" },
  { code: "ru", name: "Russian", endonym: "Русский" },
  { code: "ja", name: "Japanese", endonym: "日本語" },
  { code: "zh", name: "Mandarin Chinese", endonym: "中文" },
  { code: "ko", name: "Korean", endonym: "한국어" },
  { code: "ar", name: "Arabic", endonym: "العربية" },
  { code: "en", name: "English", endonym: "English" },
];

function detectTzOffsetHours(): number {
  // getTimezoneOffset returns minutes WEST of UTC, so flip the sign.
  return -new Date().getTimezoneOffset() / 60;
}

function detectTzName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "your local time";
  } catch {
    return "your local time";
  }
}

function fmtHourTime(hour: number): string {
  const h = String(Math.max(0, Math.min(23, Math.round(hour)))).padStart(2, "0");
  return `${h}:00`;
}

function parseHour(time: string): number | null {
  const m = /^(\d{1,2}):/.exec(time);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  if (Number.isNaN(h) || h < 0 || h > 23) return null;
  return h;
}

export default function Onboarding() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // form state
  const [name, setName] = useState("");
  const [lang, setLang] = useState("de");
  const [nativeLang, setNativeLang] = useState("en");
  const [level, setLevel] = useState("A2");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("21:00");
  const [busy, setBusy] = useState(false);

  // Target-language "…more" expansion. Same shape as the Settings
  // page — auto-expand if the current pick is outside PRIMARY_LANGS.
  const [showAllTargets, setShowAllTargets] = useState(false);
  const [showAllNatives, setShowAllNatives] = useState(false);

  const [catalog, setCatalog] = useState<LanguageOption[]>(FALLBACK_LANGS);
  const [nativeCatalog, setNativeCatalog] = useState<LanguageOption[]>(FALLBACK_LANGS);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [pickingTier, setPickingTier] = useState<PlanId | null>(null);
  const [tierErr, setTierErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .getMe()
      .then((p) => {
        setProfile(p);
        if (p.onboarding_complete && p.activated) {
          nav("/app/today", { replace: true });
          return;
        }
        // Hydrate the form from any prior partial profile.
        if (p.first_name) setName(p.first_name);
        if (p.active_language) setLang(p.active_language);
        if (p.native_language_code) setNativeLang(p.native_language_code);
        if (p.cefr_level) setLevel(p.cefr_level);
        if (typeof p.quiet_end_hour === "number") setStartTime(fmtHourTime(p.quiet_end_hour));
        if (typeof p.quiet_start_hour === "number") setEndTime(fmtHourTime(p.quiet_start_hour));
        // Returning from Stripe Checkout success → skip straight to the
        // tutor-link step. Otherwise resume at the plan picker if step 1
        // is already saved.
        if (searchParams.get("billing") === "success") {
          setStep(3);
        } else if (p.onboarding_complete && !p.activated) {
          setStep(2);
        }
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "Could not load profile"));
    api.getLanguages().then((r) => {
      if (r.languages?.length) setCatalog(r.languages);
      if (r.native_options?.length) setNativeCatalog(r.native_options);
    }).catch(() => {});
    api.getBillingStatus().then(setBilling).catch(() => {});
  }, [nav, searchParams]);

  async function pickTier(id: PlanId) {
    if (pickingTier) return;
    setTierErr(null);
    if (id === "taste") {
      setStep(3);
      return;
    }
    if (!billing?.billing_configured || !billing.tiers[id]?.has_price) {
      setTierErr("Paid plans aren't live yet — you can keep going on the trial.");
      return;
    }
    setPickingTier(id);
    try {
      const { url } = await api.createCheckout(id, { next_path: "/onboarding" });
      window.location.href = url;
    } catch (e) {
      setTierErr(e instanceof Error ? e.message : "Couldn't start checkout — try again?");
      setPickingTier(null);
    }
  }

  const langName = useMemo(
    () => catalog.find((l) => l.code === lang)?.name ?? "your language",
    [catalog, lang],
  );

  const previewName = name.trim() || "friend";
  const sampleFn = SAMPLE_BY_LANG[lang] ?? SAMPLE_BY_LANG.de;
  const previewSample = sampleFn(previewName, startTime);

  // Highlight the name token + start-time token in terracotta inside the preview.
  const highlightedPreview = useMemo(() => {
    const parts = previewSample.split(previewName);
    const out: Array<{ k: string; t: string; tc?: boolean; mono?: boolean }> = [];
    parts.forEach((p, i) => {
      if (i > 0) out.push({ k: `n${i}`, t: previewName, tc: true });
      // split each chunk by the time token for mono treatment
      const sub = p.split(startTime);
      sub.forEach((s, j) => {
        if (j > 0) out.push({ k: `t${i}-${j}`, t: startTime, mono: true });
        if (s) out.push({ k: `s${i}-${j}`, t: s });
      });
    });
    return out;
  }, [previewSample, previewName, startTime]);

  const canContinue =
    name.trim().length > 0 && !!lang && !!level && !!startTime && !!endTime;

  async function saveStep1() {
    if (!canContinue || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const startH = parseHour(startTime) ?? 9;
      const endH = parseHour(endTime) ?? 21;
      const updated = await api.updateMe({
        first_name: name.trim(),
        active_language: lang,
        native_language_code: nativeLang,
        cefr_level: level,
        // Quiet hours are the inverse of the messageable window:
        //   message me between START and END  ⇒  quiet outside that range.
        quiet_end_hour: startH,
        quiet_start_hour: endH,
        tz_offset_hours: detectTzOffsetHours(),
        onboarding_complete: true,
      });
      setProfile(updated);
      // After saving step 1, hop to the plan picker — even returning
      // users in the middle of onboarding always see the picker once
      // before the tutor link, so they have a real chance to upgrade
      // beyond the trial default.
      setStep(2);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save — try again?");
    } finally {
      setBusy(false);
    }
  }

  if (err && !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-terracotta">{err}</p>
      </div>
    );
  }
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Logo size="lg" />
      </div>
    );
  }

  const tzName = detectTzName();

  return (
    <div className="min-h-screen relative">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <Logo size="md" />

        {/* Step indicator — terracotta on active, faint on inactive, hairline rule between */}
        <div className="mt-10 mb-9 flex items-center gap-4">
          <span
            className={cn(
              "font-mono text-[11px] tracking-[0.18em] uppercase whitespace-nowrap",
              step === 1 ? "text-terracotta" : "text-ink-faint",
            )}
          >
            01 · Tell us
          </span>
          <span className="flex-1 h-px bg-[rgba(42,36,26,0.18)]" />
          <span
            className={cn(
              "font-mono text-[11px] tracking-[0.18em] uppercase whitespace-nowrap",
              step === 2 ? "text-terracotta" : "text-ink-faint",
            )}
          >
            02 · Pick a plan
          </span>
          <span className="flex-1 h-px bg-[rgba(42,36,26,0.18)]" />
          <span
            className={cn(
              "font-mono text-[11px] tracking-[0.18em] uppercase whitespace-nowrap",
              step === 3 ? "text-terracotta" : "text-ink-faint",
            )}
          >
            03 · Meet your tutor
          </span>
        </div>

        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Live preview card */}
            <div className="grid md:grid-cols-[1fr_180px] gap-6 md:gap-8 items-center mb-12">
              <div className="rounded-md bg-ink-card text-paper p-5">
                <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-paper/45 mb-2">
                  A sample first message — updates as you fill this in
                </div>
                <p className="font-serif text-[18px] md:text-[19px] leading-snug text-paper">
                  {highlightedPreview.map((p) =>
                    p.tc ? (
                      <span key={p.k} className="italic text-terracotta-soft font-medium">
                        {p.t}
                      </span>
                    ) : p.mono ? (
                      <span key={p.k} className="font-mono text-[14px] text-paper">
                        {p.t}
                      </span>
                    ) : (
                      <span key={p.k}>{p.t}</span>
                    ),
                  )}
                </p>
              </div>
              <div className="font-mono text-[11px] text-ink-faint leading-snug">
                ↓ adjust below to taste
              </div>
            </div>

            {/* Mad-libs form */}
            <div className="space-y-10">
              {/* Name + language */}
              <div>
                <div className="font-serif text-[26px] md:text-[28px] leading-snug">
                  My name is{" "}
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="your name"
                    aria-label="Your name"
                    className="font-serif italic text-[26px] md:text-[28px] bg-transparent border-0 border-b-2 border-ink focus:outline-none focus:border-terracotta px-1 py-0 min-w-[160px] w-[220px] placeholder:text-ink-faint/60"
                  />{" "}
                  and I want to learn
                </div>
                {(() => {
                  // Same top-5 + "…more" pattern as Settings so the two
                  // pickers feel identical.
                  const primary = PRIMARY_LANGS
                    .map((c) => catalog.find((l) => l.code === c))
                    .filter((l): l is LanguageOption => !!l);
                  const rest = catalog.filter((l) => !PRIMARY_LANGS.includes(l.code));
                  const selectedInRest = rest.some((l) => l.code === lang);
                  const expanded = showAllTargets || selectedInRest;
                  const visible = expanded ? [...primary, ...rest] : primary;
                  return (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {visible.map((opt) => {
                        const active = lang === opt.code;
                        return (
                          <button
                            key={opt.code}
                            type="button"
                            onClick={() => setLang(opt.code)}
                            className={cn(
                              "px-3.5 py-1 rounded-full border font-sans text-[13px] transition-colors",
                              active
                                ? "border-terracotta bg-terracotta text-paper"
                                : "border-[rgba(42,36,26,0.18)] text-ink-soft hover:border-terracotta hover:text-terracotta",
                            )}
                          >
                            {opt.name}
                          </button>
                        );
                      })}
                      {!expanded && rest.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowAllTargets(true)}
                          className="px-3 py-1 rounded-full border border-dashed border-[rgba(42,36,26,0.25)] font-sans text-[13px] text-ink-faint hover:border-terracotta hover:text-terracotta transition-colors"
                        >
                          …more ({rest.length})
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Native language — prominently visible, right below the
                  target row. Server used to default to English silently;
                  now the user can pick during signup. Same top-5+more
                  UX as target so it's one visual pattern to learn. */}
              <div className="mt-8">
                <div className="font-serif text-[22px] md:text-[24px] leading-snug">
                  I speak
                </div>
                {(() => {
                  const src = nativeCatalog.length ? nativeCatalog : catalog;
                  const primary = PRIMARY_LANGS
                    .map((c) => src.find((l) => l.code === c))
                    .filter((l): l is LanguageOption => !!l);
                  const rest = src.filter((l) => !PRIMARY_LANGS.includes(l.code));
                  const selectedInRest = rest.some((l) => l.code === nativeLang);
                  const expanded = showAllNatives || selectedInRest;
                  const visible = expanded ? [...primary, ...rest] : primary;
                  return (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {visible.map((opt) => {
                        const active = nativeLang === opt.code;
                        return (
                          <button
                            key={opt.code}
                            type="button"
                            onClick={() => setNativeLang(opt.code)}
                            className={cn(
                              "px-3.5 py-1 rounded-full border font-sans text-[13px] transition-colors",
                              active
                                ? "border-terracotta bg-terracotta text-paper"
                                : "border-[rgba(42,36,26,0.18)] text-ink-soft hover:border-terracotta hover:text-terracotta",
                            )}
                          >
                            {opt.name}
                          </button>
                        );
                      })}
                      {!expanded && rest.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowAllNatives(true)}
                          className="px-3 py-1 rounded-full border border-dashed border-[rgba(42,36,26,0.25)] font-sans text-[13px] text-ink-faint hover:border-terracotta hover:text-terracotta transition-colors"
                        >
                          …more ({rest.length})
                        </button>
                      )}
                    </div>
                  );
                })()}
                <p className="mt-2 font-serif italic text-ink-faint text-[13px]">
                  glosses appear in your native
                </p>
              </div>

              {/* CEFR */}
              <div>
                <div className="font-serif text-[24px] md:text-[26px] leading-snug">
                  I'd say I'm at
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {CEFR_OPTIONS.map((lv) => {
                    const active = level === lv;
                    return (
                      <button
                        key={lv}
                        type="button"
                        onClick={() => setLevel(lv)}
                        className={cn(
                          "px-3 py-1 rounded-full border font-sans text-[13px] tabular-nums transition-colors min-w-[44px]",
                          active
                            ? "border-terracotta bg-terracotta text-paper"
                            : "border-[rgba(42,36,26,0.18)] text-ink-soft hover:border-terracotta hover:text-terracotta",
                        )}
                      >
                        {lv}
                      </button>
                    );
                  })}
                  <span className="ml-2 font-serif italic text-ink-faint text-[14px]">
                    — don't know? we'll calibrate.
                  </span>
                </div>
              </div>

              {/* Time window */}
              <div>
                <div className="font-serif text-[24px] md:text-[26px] leading-snug">
                  Message me between
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    aria-label="Start of messageable window"
                    className="font-mono text-[16px] px-3 py-1.5 rounded-md border border-[rgba(42,36,26,0.18)] bg-paper focus:outline-none focus:border-terracotta"
                  />
                  <span className="font-serif italic text-ink-faint text-[16px]">and</span>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    aria-label="End of messageable window"
                    className="font-mono text-[16px] px-3 py-1.5 rounded-md border border-[rgba(42,36,26,0.18)] bg-paper focus:outline-none focus:border-terracotta"
                  />
                  <span className="font-serif italic text-ink-faint text-[14px]">
                    — your time ({tzName})
                  </span>
                </div>
              </div>
            </div>

            {err && <div className="mt-6 text-terracotta text-sm">{err}</div>}

            {/* CTA */}
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Button
                size="lg"
                onClick={saveStep1}
                disabled={!canContinue || busy}
              >
                {busy ? "Saving…" : "Looks right"} <ArrowRight size={16} />
              </Button>
              <span className="font-serif italic text-ink-faint text-[14px]">
                you can change all of this later
              </span>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="font-serif italic text-ink-soft text-[15px]">step 02</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-2 leading-tight">
              Pick a <span className="italic-accent">plan</span>.
            </h2>
            <p className="mt-4 text-ink-soft leading-relaxed max-w-lg">
              Trial is on the house — no card. Or skip the wait and start on a paid tier.
              Cancel anytime, one click, 14-day refund on paid plans.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {PLAN_ORDER.map((id) => {
                const t = billing?.tiers[id];
                const popular = id === "trio";
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => pickTier(id)}
                    disabled={!!pickingTier}
                    className={cn(
                      "relative rounded-lg p-5 flex flex-col text-left transition-shadow",
                      popular
                        ? "bg-ink-card text-paper shadow-float hover:shadow-lg"
                        : "bg-paper border border-[rgba(42,36,26,0.15)] hover:border-terracotta",
                      pickingTier && pickingTier !== id ? "opacity-40" : "",
                    )}
                  >
                    {popular && (
                      <div className="absolute -top-2.5 left-5 px-2 py-0.5 bg-terracotta text-paper text-[0.6rem] tracking-[0.16em] uppercase rounded">
                        recommended
                      </div>
                    )}
                    <div className="font-serif text-2xl">{t?.name ?? id}</div>
                    <div className={cn("text-xs italic mt-0.5", popular ? "text-paper/60" : "text-ink-faint")}>
                      {PLAN_TAGLINES[id]}
                    </div>
                    <div className="mt-5 flex items-baseline gap-1">
                      <span className="font-serif text-4xl leading-none">
                        {t?.price_usd === 0 ? "Free" : t?.price_usd ? `$${t.price_usd.toFixed(2)}` : "—"}
                      </span>
                      {!!t?.price_usd && t.price_usd > 0 && (
                        <span className={cn("text-xs ml-1", popular ? "text-paper/50" : "text-ink-faint")}>/ mo</span>
                      )}
                    </div>
                    <ul className={cn("mt-4 space-y-1.5 text-[13px] flex-1", popular ? "text-paper/80" : "text-ink-soft")}>
                      <li className="flex items-start gap-2">
                        <Check size={12} className={cn("mt-1 shrink-0", popular ? "text-terracotta-soft" : "text-terracotta")} />
                        <span>{t?.turns ?? "—"} turns/day</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check size={12} className={cn("mt-1 shrink-0", popular ? "text-terracotta-soft" : "text-terracotta")} />
                        <span>{t && t.voice > 0 ? `${t.voice} voice notes/mo` : "Text only"}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check size={12} className={cn("mt-1 shrink-0", popular ? "text-terracotta-soft" : "text-terracotta")} />
                        <span>
                          {!t ? "—" : t.languages > 3 ? "All languages" : t.languages > 1 ? `${t.languages} languages` : "1 language"}
                        </span>
                      </li>
                    </ul>
                    <span
                      className={cn(
                        "mt-5 inline-flex items-center gap-1 font-mono text-[11px] tracking-[0.16em] uppercase",
                        popular ? "text-paper" : "text-terracotta",
                      )}
                    >
                      {pickingTier === id
                        ? "Redirecting…"
                        : id === "taste"
                          ? "Start trial →"
                          : "Choose →"}
                    </span>
                  </button>
                );
              })}
            </div>

            {tierErr && <p className="mt-6 text-sm text-terracotta">{tierErr}</p>}

            {!billing?.billing_configured && (
              <p className="mt-6 text-xs text-ink-faint">
                Paid plans aren't enabled on this deployment yet — you can still keep going on the trial.
              </p>
            )}

            <button
              type="button"
              onClick={() => setStep(1)}
              disabled={!!pickingTier}
              className="mt-8 text-ink-soft text-[13px] underline decoration-ink-faint/40 hover:decoration-terracotta"
            >
              ← back
            </button>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="font-serif italic text-ink-soft text-[15px]">step 03</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-2 leading-tight">
              Now <span className="italic-accent">meet</span> your tutor.
            </h2>
            <p className="mt-4 text-ink-soft leading-relaxed max-w-lg">
              ilmli lives in Telegram. Open the link below and tap{" "}
              <span className="font-mono text-[13px]">Start</span> — your tutor will be in
              touch in <span className="font-serif italic text-terracotta">{langName}</span>.
            </p>

            <div className="mt-8 card max-w-xl">
              <LinkCard langCode={profile.active_language ?? lang} />
            </div>

            {profile.activated && (
              <p className="mt-6 text-[#386641] text-sm">
                You're connected.{" "}
                <a href="/app/today" className="font-medium underline">
                  Go to your dashboard →
                </a>
              </p>
            )}

            <div className="mt-10 pt-6 border-t border-[rgba(42,36,26,0.12)]">
              <p className="font-serif italic text-ink-faint text-[14px] leading-relaxed max-w-lg">
                <span className="text-ink-soft not-italic font-medium">Plan?</span> {!billing || billing.tier === "taste"
                  ? "You're on the free 7-day trial. Change plans any time from your dashboard."
                  : `You're on ${billing.tier_name}. Welcome aboard.`}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setStep(2)}
              className="mt-6 text-ink-soft text-[13px] underline decoration-ink-faint/40 hover:decoration-terracotta"
            >
              ← back
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
